// Match Day engine — FRONTEND COPY of services/matchday/minutes.js + topup.js + the merge rule
// in services/matchday/sync.js (CRA can't import outside src/). Pinned to the backend by
// engine.parity.test.js: change both or the test fails. The iPad runs this offline, so the live
// minutes and the top-up need no connection.
const HALF_S = 45 * 60;

const toMs = v => {
  if (v == null || v === '') return null;
  const ms = typeof v === 'number' ? v : new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
};

function normalizeClock(clock) {
  return {
    h1Start: toMs(clock && clock.h1Start), h1End: toMs(clock && clock.h1End),
    h2Start: toMs(clock && clock.h2Start), h2End: toMs(clock && clock.h2End),
  };
}

// 'pre' → 'h1' → 'ht' → 'h2' → 'ft', from the latest clock step that is set.
function matchPhase(clock) {
  const c = normalizeClock(clock);
  if (c.h2End != null) return 'ft';
  if (c.h2Start != null) return 'h2';
  if (c.h1End != null) return 'ht';
  if (c.h1Start != null) return 'h1';
  return 'pre';
}

// Seconds played in a half so far (live while it runs, final once it has ended; 0 before it starts).
function halfDurationS(clock, half, now) {
  const c = normalizeClock(clock);
  const start = half === 1 ? c.h1Start : c.h2Start;
  if (start == null) return 0;
  const endSet = half === 1 ? c.h1End : c.h2End;
  const end = endSet != null ? endSet : toMs(now);
  if (end == null) return 0;
  return Math.max(0, (end - start) / 1000);
}

// Where a wall-clock instant falls on the match clock — used when a sub is logged and when its
// time is corrected. Half-time (after the 1st half ended, before the 2nd started) is half 2 at 0;
// anything before kick-off is half 1 at 0; times past the end of a half clamp to its end.
function placeOnClock(clock, occurredAt) {
  const c = normalizeClock(clock);
  const t = toMs(occurredAt);
  if (t == null) return { half: 1, elapsedInHalfS: 0 };
  const round = s => Math.round(s * 1000) / 1000;
  const clampTo = (s, start, end) => round(Math.max(0, end != null ? Math.min(s, (end - start) / 1000) : s));
  if (c.h2Start != null && t >= c.h2Start) return { half: 2, elapsedInHalfS: clampTo((t - c.h2Start) / 1000, c.h2Start, c.h2End) };
  if (c.h1End != null && t >= c.h1End) return { half: 2, elapsedInHalfS: 0 };
  if (c.h1Start != null && t >= c.h1Start) return { half: 1, elapsedInHalfS: clampTo((t - c.h1Start) / 1000, c.h1Start, c.h1End) };
  return { half: 1, elapsedInHalfS: 0 };
}

// A corrected copy of an event: new wall-clock time, half/elapsed re-derived from the clock.
function correctEventTime(event, clock, occurredAt, clientUpdatedAt) {
  const iso = new Date(toMs(occurredAt)).toISOString();
  return { ...event, occurredAt: iso, ...placeOnClock(clock, iso), clientUpdatedAt };
}

function sortHalfEvents(events) {
  return events.slice().sort((a, b) =>
    (Number(a.elapsedInHalfS) - Number(b.elapsedInHalfS)) ||
    ((toMs(a.occurredAt) || 0) - (toMs(b.occurredAt) || 0)) ||
    String(a.id).localeCompare(String(b.id)));
}

// Per player: seconds per half, total, and where he is now ('on' | 'off' | 'bench').
// Players named in events but missing from the squad are still counted.
function computeMinutes({ clock, squad, events, now }) {
  const c = normalizeClock(clock);
  const phase = matchPhase(clock);
  const live = (events || []).filter(e => !e.deletedAt);
  const activeSquad = (squad || []).filter(s => !s.deletedAt);

  const players = new Map();
  const ensure = id => {
    const key = Number(id);
    if (!players.has(key)) players.set(key, { playerId: key, role: null, h1Seconds: 0, h2Seconds: 0, played: false });
    return players.get(key);
  };
  activeSquad.forEach(s => { ensure(s.playerId).role = s.role; });

  const onPitch = new Map(); // playerId → second of the current half he came on
  activeSquad.filter(s => s.role === 'starter').forEach(s => { onPitch.set(Number(s.playerId), 0); ensure(s.playerId).played = true; });

  const durations = { h1S: halfDurationS(clock, 1, now), h2S: halfDurationS(clock, 2, now) };
  // Half-2 events only count once the 1st half is over; one that exists earlier (a clock step
  // undone after it was logged) waits until the clock gets there again.
  const halves = [1];
  if (c.h1End != null || c.h2Start != null) halves.push(2);

  for (const half of halves) {
    const D = half === 1 ? durations.h1S : durations.h2S;
    const field = half === 1 ? 'h1Seconds' : 'h2Seconds';
    for (const e of sortHalfEvents(live.filter(ev => Number(ev.half) === half))) {
      const at = Math.min(Math.max(0, Number(e.elapsedInHalfS) || 0), D);
      if (e.playerOff != null) {
        const off = Number(e.playerOff);
        const p = ensure(off);
        if (onPitch.has(off)) { p[field] += at - onPitch.get(off); onPitch.delete(off); }
      }
      if (e.playerOn != null) {
        const on = Number(e.playerOn);
        const p = ensure(on);
        if (!onPitch.has(on)) { onPitch.set(on, at); p.played = true; }
      }
    }
    for (const [id, enteredAt] of onPitch) {
      ensure(id)[field] += D - enteredAt;
      onPitch.set(id, 0);
    }
  }

  const rows = [...players.values()].map(p => ({
    playerId: p.playerId,
    role: p.role,
    h1Seconds: p.h1Seconds,
    h2Seconds: p.h2Seconds,
    seconds: p.h1Seconds + p.h2Seconds,
    status: onPitch.has(p.playerId) ? 'on' : p.played ? 'off' : 'bench',
  }));
  return { phase, durations, players: rows };
}

// The big match clock: { phase, seconds } with half 2 shown as 45:00 + elapsed.
function displayClock(clock, now) {
  const phase = matchPhase(clock);
  if (phase === 'pre') return { phase, seconds: 0 };
  if (phase === 'h1') return { phase, seconds: halfDurationS(clock, 1, now) };
  if (phase === 'ht') return { phase, seconds: HALF_S };
  return { phase, seconds: HALF_S + halfDurationS(clock, 2, now) };
}

// An event's position on the display clock, for the log ("match 55:00").
function eventMatchSeconds(event) {
  const e = Number(event.elapsedInHalfS) || 0;
  return Number(event.half) === 2 ? HALF_S + e : e;
}

// 5835.4 → "97:15" (whole seconds, floored; minutes are not wrapped at 60).
function formatMinSec(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── Top-up (services/matchday/topup.js) ─────────────────────────────────────────────────────
const DEFAULTS = {
  TOPUP_CUTOFF_MIN: 45, RUN_LENGTH_M: 60, RUN_RANGE_M: [55, 65], MAX_RUNS: 8, OVERRIDE_GPS_WARN_PCT: 10,
  GPS_MINUTES_DIFF_MIN: 2,
};

const runsFor = (m, len) => (m > 0 ? Math.ceil(Math.round(m * 10) / 10 / len) : 0);

// One player. minutesRow: { seconds, status } from computeMinutes; phase from it too.
function topupRow({ seconds, status, phase, targetM, overrideM, gpsHidM, gpsMinutes, config = DEFAULTS }) {
  const cfg = { ...DEFAULTS, ...config };
  const minutes = (Number(seconds) || 0) / 60;
  const required = minutes < cfg.TOPUP_CUTOFF_MIN;
  const provisional = phase !== 'ft' && (status === 'on' || status === 'bench');
  const T = targetM != null && Number(targetM) > 0 ? Number(targetM) : null;

  const estimateM = T == null ? null : T * (1 - Math.min(minutes, 90) / 90);
  let doneM = null;
  let basis = 'estimate';
  if (overrideM != null) { doneM = Number(overrideM); basis = 'manual'; }
  else if (gpsHidM != null) { doneM = Number(gpsHidM); basis = 'gps'; }

  let neededM = null;
  if (T != null) neededM = !required ? 0 : doneM != null ? Math.max(0, T - doneM) : estimateM;
  const [lo, hi] = cfg.RUN_RANGE_M;
  const cap = r => (r == null ? null : Math.min(r, cfg.MAX_RUNS));
  const runsUncapped = neededM == null ? null : runsFor(neededM, cfg.RUN_LENGTH_M);
  const runs = cap(runsUncapped);
  const gpsMismatch = overrideM != null && gpsHidM != null && Number(gpsHidM) > 0
    && Math.abs(Number(overrideM) - Number(gpsHidM)) / Number(gpsHidM) * 100 > cfg.OVERRIDE_GPS_WARN_PCT;

  return {
    minutes, status, required, provisional, targetM: T, noTarget: T == null,
    basis, doneM, estimateM, neededM, runs,
    runsMin: neededM == null ? null : cap(runsFor(neededM, hi)),
    runsMax: neededM == null ? null : cap(runsFor(neededM, lo)),
    runsUncapped, capped: runsUncapped != null && runsUncapped > cfg.MAX_RUNS,
    gpsMismatch,
    gpsMinutes: gpsMinutes != null ? Number(gpsMinutes) : null,
    gpsMinutesDiff: gpsMinutes != null && Math.abs(minutes - Number(gpsMinutes)) > cfg.GPS_MINUTES_DIFF_MIN,
  };
}

// The whole table. players: state.players (with isGoalkeeper, hidTargetM); squad: state.squad;
// overrides: state.overrides; minutes: computeMinutes(...) result; gps: state.gps, i.e.
// { [playerId]: { hidM, minutes } } once the match's GPS is uploaded (optional).
function topupTable({ players, squad, overrides, minutes, gps = {}, config = DEFAULTS }) {
  const byPlayer = new Map((players || []).map(p => [Number(p.id), p]));
  const minutesBy = new Map((minutes.players || []).map(m => [Number(m.playerId), m]));
  const overrideBy = new Map((overrides || []).filter(o => !o.deletedAt).map(o => [Number(o.playerId), Number(o.hidM)]));
  return (squad || [])
    .filter(s => !s.deletedAt)
    .map(s => ({ s, p: byPlayer.get(Number(s.playerId)) }))
    .filter(({ p }) => p && !p.isGoalkeeper)
    .map(({ s, p }) => {
      const m = minutesBy.get(Number(p.id)) || { seconds: 0, status: 'bench' };
      const typed = s.hidTargetOverrideM != null ? Number(s.hidTargetOverrideM) : null;
      return {
        playerId: p.id, name: p.name, squadNumber: p.squadNumber, position: p.position, role: s.role,
        positionTargetM: p.hidTargetM, typedTarget: typed != null,
        ...topupRow({
          seconds: m.seconds, status: m.status, phase: minutes.phase,
          targetM: typed != null ? typed : p.hidTargetM,
          overrideM: overrideBy.has(Number(p.id)) ? overrideBy.get(Number(p.id)) : null,
          gpsHidM: gps && gps[p.id] ? gps[p.id].hidM : null,
          gpsMinutes: gps && gps[p.id] ? gps[p.id].minutes : null,
          config,
        }),
      };
    })
    .filter(r => r.required)   // 45+ min (starter or sub) → off the list
    .sort((a, b) => (a.minutes - b.minutes) || String(a.name).localeCompare(String(b.name)));
}

// ── Merge rule (services/matchday/sync.js) ──────────────────────────────────────────────────
function isNewer(incoming, existing) {
  if (!existing) return true;
  return new Date(incoming.clientUpdatedAt).getTime() > new Date(existing.clientUpdatedAt).getTime();
}
function mergeByKey(local, remote, keyOf) {
  const out = new Map();
  (local || []).forEach(r => out.set(keyOf(r), r));
  (remote || []).forEach(r => {
    const k = keyOf(r);
    if (isNewer(r, out.get(k))) out.set(k, r);
  });
  return [...out.values()];
}
function metresOrNull(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

export {
  HALF_S, normalizeClock, matchPhase, halfDurationS, placeOnClock, correctEventTime,
  computeMinutes, displayClock, eventMatchSeconds, formatMinSec,
  DEFAULTS as TOPUP_DEFAULTS, topupRow, topupTable,
  isNewer, mergeByKey, metresOrNull,
};
